<template>
  <div class="actions">
    <a-popover
      v-if="hasEmoji"
      v-model:visible="showEmojiPopover"
      :title="null"
      overlayClassName="emoji-popover"
      trigger="click"
      placement="topLeft"
      arrowPointAtCenter
    >
      <ActionButton class="box" type="emoji" @click="showEmojiPopover = true" />

      <template #content>
        <EmojiPicker
          native
          hide-search
          hide-group-icons
          disable-skin-tones
          :group-names="groupNames"
          @select="handleSelectEmoji"
        />
      </template>
    </a-popover>

    <label for="file-input" v-if="hasFile">
      
      <ActionButton
        :disabled="(Boolean(file) && !multiple) || (multiple && disabledMaxFilesPerType('file', files))"
        class="box" type="file" prevent-event
        :maxFileSizeMB="maxFileSizeMB"
      />

      <input
        ref="fileInputRef"
        id="file-input"
        type="file"
        :multiple="multiple"
        :disabled="(Boolean(file) && !multiple) || (multiple && disabledMaxFilesPerType('file', files))"
        :accept="supportedFileTypes.join()"
        @change="handleFileInputChange($event)"
      />
    </label>

    <label for="media-input" v-if="hasMedia">
      <ActionButton :disabled="(Boolean(file) && !multiple)  || (multiple && disabledMaxFilesPerType('media', files))" class="box" type="media" 
        prevent-event :tooltip="tooltipMedia" :maxFileSizeMB="maxFileSizeMB" />
      
      <input
        ref="mediaInputRef"
        id="media-input"
        type="file"
        :multiple="multiple"
        :disabled="(Boolean(file) && !multiple)  || (multiple && disabledMaxFilesPerType('media', files))"
        :accept="supportedMediaTypes.join()"
        @change="handleFileInputChange($event)"
      />
    </label>

    <ActionButton 
      v-if="hasTemplate"
      :disabled="isTemplateButtonDisabled"
      :tooltip="templateButtonTooltip"
      :is_agent="isTemplateLimitReached || user?.role?.name === 'Agente' || !isTemplateButtonDisabled"
      :content="templateButtonPopover"
      class="box" 
      type="template" 
      @click="showTemplateModal = true" 
    />
    <EmailTemplatesModal
      v-if="hasTemplate"
      v-model:visible="showTemplateModal"
      :editor-html="editorHtml"
      @insert-templates="handleInsertTemplates"
      @template-limit-reached="handleTemplateLimitReached"
    />
  </div>

  <div class="file" v-if="file && !multiple">
    <p>
      <PaperClipOutlined style="font-size: 18px" v-if="hasFile" />
      <img v-else :src="attachImageIcon" draggable="false" class="icon-media" />

      <span class="name">{{ getFileName(typeof file === "string" && fileName ? fileName : file) }}</span>
    </p>

    <a-button type="link" class="button" danger @click="handleDelete">
      <DeleteOutlined />
    </a-button>
  </div>

  <CustomScrollbar
    :height="validateTotalSize ? 'calc(100vh - 650px)' : (filesReduce ? '120px' : '220px')"
    auto
    v-if="multiple"
  >
    <div class="files">
      <AttachmentUploadStatus 
        v-if="validateTotalSize"
        :files="files"
      />

      <div class="file" v-for="file, pos in files" :key="pos">
        <p>
          <PaperClipOutlined style="font-size: 18px" v-if="hasFile" />
          <img v-else :src="attachImageIcon" draggable="false" class="icon-media" />

          <span class="name">{{ getFileName(file) }}</span>
        </p>

        <a-button type="link" class="button" danger @click="handleDelete(pos)">
          <DeleteOutlined />
        </a-button>
      </div>
    </div>
  </CustomScrollbar>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { message } from "ant-design-vue";
import { PaperClipOutlined, DeleteOutlined } from "@ant-design/icons-vue";
import EmojiPicker from "vue3-emoji-picker";
import "vue3-emoji-picker/css";

import { Emoji } from "@/@types/workspace";
import { groupNames } from "@/app/workspace/utils/emojiPickerOptions";
import {
  supportedMediaTypes,
  supportedFileTypes,
} from "@/app/workspace/components/chat/input/supportedFiles";

import ActionButton from "@/app/workspace/components/chat/input/ActionButton.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import EmailTemplatesModal from "@/app/emails/components/modals/EmailTemplatesModal.vue";
import attachImageIcon from "@/app/workspace/assets/chat/attach-image-icon.svg?url";
import { portfolioService } from "@/app/entities/services/Portfolio.service";
import { useFileHandler } from "../../composables/useFileHandler";
import AttachmentUploadStatus from "../attachments/AttachmentUploadStatus.vue";
import { UserData } from "@/@types/global";
import { useStore } from "vuex";

// const OneMegaByte = 1_000_000;

const props = withDefaults(
  defineProps<{
    file: string | File | undefined;
    // Nombre a mostrar cuando `file` es una url ya subida
    fileName?: string | null;
    files: string[] | File[] | undefined;
    multiple: boolean;
    hasEmoji: boolean;
    hasFile: boolean;
    hasMedia: boolean;
    hasTemplate: boolean;
    editorHtml?: string;
    tooltipMedia?: string;
    validatePerType: boolean;
    filesReduce?: boolean;

    validateTotalSize?: boolean;
    maxTotalSizeMB?: number;
    maxFileSizeMB?: number;
  }>(),
  {
    multiple: false,
    files: [],
    hasEmoji: true,
    hasFile: true,
    hasMedia: true,
    hasTemplate: false,
    editorHtml: "",
    tooltipMedia: undefined,
    validatePerType: true,
    filesReduce: false,

    validateTotalSize: false,
    maxTotalSizeMB: 10,
    maxFileSizeMB: 5,
  }
);

const emit = defineEmits<{
  selectEmoji: [emoji: string];
  "update:file": [file: File | undefined];
  "update:files": [files: File[]];
  insertTemplate: [templatesHtml: string, templatesCount: number];
}>();

const {
  disabledMaxFilesPerType, 
  validateFileMultiple,
  validateFile,
  filesValidatedByType,
} = useFileHandler({
  multiple: props.multiple,
  validatePerType: props.validatePerType,

  validateTotalSize: props.validateTotalSize,
  maxTotalSizeMB: props.maxTotalSizeMB,
  maxFileSizeMB: props.maxFileSizeMB,
});

const showEmojiPopover = ref(false);
const showTemplateModal = ref(false);
const isTemplateButtonDisabled = ref(true);
const templateButtonTooltip = ref("");
const templateButtonPopover = ref("");
const isTemplateLimitReached = ref(false);
const fileInputRef = ref<HTMLInputElement>();
const mediaInputRef = ref<HTMLInputElement>();
const TEMPLATE_LIMIT_REACHED_TOOLTIP =
  "Alcanzaste el máximo de 3 plantillas por correo. Puedes quitar una plantilla desde el cuerpo del correo para insertar otra.";

const getFileName = (file: File | string) => {
  let fileName;
  if (typeof file === "string") fileName = file.split("/").at(-1);
  else fileName = file?.name;

  if (!fileName) return '';

  const maxLength = 43;
  const extension = fileName.split('.').pop() || '';
  const name = fileName.replace(`.${extension}`, '');

  if (name.length <= maxLength) {
    return fileName;
  }

  return `${name.slice(0, maxLength)}...${name.slice(-5)}.${extension}`;
};

const handleSelectEmoji = (emojiMetadata: Emoji) => {
  const emojiChar = emojiMetadata.i;
  emit("selectEmoji", emojiChar);
  showEmojiPopover.value = false;
};

const handleInsertTemplates = (templatesHtml: string, templatesCount: number) => {
  emit("insertTemplate", templatesHtml, templatesCount);
};

const setTemplateButtonState = async () => {
  if (!props.hasTemplate) return;
  if (isTemplateLimitReached.value) {
    isTemplateButtonDisabled.value = true;
    templateButtonPopover.value = "";
    templateButtonTooltip.value = TEMPLATE_LIMIT_REACHED_TOOLTIP;
    return;
  }

  try {
    const portfolios = await portfolioService.getPortfolioWithTemplates();
    const hasActiveTemplates = portfolios.some((p) => p.templates_active_count > 0);
    const hasInactiveTemplates = portfolios.some((p) => p.templates_inactive_count > 0);

    isTemplateButtonDisabled.value = !hasActiveTemplates;

    if (hasActiveTemplates) {
      templateButtonPopover.value = "";
      templateButtonTooltip.value = "Insertar plantilla";
    } else if (hasInactiveTemplates) {
      templateButtonPopover.value = "No hay plantillas activas en tus campañas del buzón de correo. Actívalas desde";
      templateButtonTooltip.value = "No hay plantillas activas en tus campañas del buzón de correo.";
    } else {
      templateButtonPopover.value = "No hay plantillas asociadas a tus campañas del buzón de correo. Asocialas desde";
      templateButtonTooltip.value = "No hay plantillas asociadas a tus campañas del buzón de correo.";
    }
  } catch (error) {
    console.error(error);
    isTemplateButtonDisabled.value = true;
  }
};

const handleTemplateLimitReached = (value: boolean) => {
  isTemplateLimitReached.value = value;
  setTemplateButtonState();
};

const handleFileInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;

  console.log('draft files handleFileInputChange');
  try {
    if (!props.multiple) {
      validateFile(target.files?.[0]);
      const selectedFile = target.files?.[0];
      emit("update:file", selectedFile);
    } else if (props.multiple && target?.files && target?.files?.length > 0) {
      const currentFiles = props.files || [];

      const filesValidated = validateFileMultiple(
        target.files,
        currentFiles
      );
      
      const validatedByType = filesValidatedByType(filesValidated, currentFiles)

      if (validatedByType.length < filesValidated.length && props.validatePerType) {
        message.error("Se excedió el límite máximo de 5 archivos por tipo (imágenes, videos o documentos)");
      } else if (validatedByType.length < filesValidated.length && !props.validatePerType) {
        message.error("Se excedió el límite máximo de 5 archivos");
      }

      if (validatedByType.length > 0) {
        const files = [...props.files, ...validatedByType];
        emit("update:files", files)
      }
      
      target.value = "";
    }
  } catch (error) {
    console.error(error);
    message.error(error as string);
  }
};

const handleDelete = (pos: number) => {
  emit("update:file", undefined);
  if (props.multiple) {
    const files = props.files;
    console.log(files, 'files 1');
    files.splice(pos, 1)
    console.log(files, 'files 2');
    emit("update:files", files);
  }
  if (fileInputRef.value) fileInputRef.value.value = "";
  if (mediaInputRef.value) mediaInputRef.value.value = "";
};

const store = useStore();
const user = computed<UserData>(() => store.getters["getDataUser"]);

onMounted(() => {
  setTemplateButtonState();
});
</script>

<style scoped lang="sass">
.button
  padding: 0
  height: 22px

.actions
  display: flex
  align-items: center
  gap: 4px
  margin-top: 8px

#file-input, #media-input
  display: none

:deep(.box)
  border-radius: 4px
  background-color: $gray-3
  overflow: hidden

:deep(.box img)
  width: 18px

.files
  padding: 0 10px 0 0
.file
  display: flex
  justify-content: space-between
  align-items: center
  background-color: $geekblue-1
  border-radius: 4px
  padding: 0 8px
  margin-top: 12px
  color: $gray-8

  p
    margin: 0
    display: flex
    align-items: center
    gap: 4px

  .name
    color: #4096FF

.icon-media
  font-size: 16px
  margin-left: -6px


</style>

<style>
.emoji-popover .ant-popover-inner-content {
  padding: 0 !important;
}

.v3-emoji-picker {
  box-shadow: none !important;
}

.v3-emoji-picker.v3-color-theme-light .v3-footer {
  display: none;
}
</style>
